//frontend/src/pages/Home.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../App.css"; // Ensure styles are included
import axios from 'axios';
import hero from "../images/G-lawhub_3.png";
import heroAlt from "../images/G-lawhub_4.png";
import Chatbot from "../components/Chatbot";

const Home = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");

  // Redirect authenticated users to Services page only on initial page load
  useEffect(() => {
    const token = localStorage.getItem('token');
    const hasRedirected = sessionStorage.getItem('hasRedirectedToServices');
    
    if (token && !hasRedirected) {
      // User is authenticated and hasn't been redirected yet
      sessionStorage.setItem('hasRedirectedToServices', 'true');
      navigate('/services');
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get('/api/auth/profile', {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => {
      const u = res.data?.user;
      if (u) {
        setUserName(u.firstName);
      }
    })
    .catch(() => {/* ignore profile fetch errors */});
  }, []);

  // Hero image rotator (3-second interval with cross-fade)
  const heroImages = [hero, heroAlt];
  const heroAlts = ["Learning law illustration", "Alternate legal learning illustration"];
  const [currentHero, setCurrentHero] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setCurrentHero((i) => (i + 1) % heroImages.length);
    }, 3000); // 3 seconds
    return () => clearInterval(id);
  }, []);

  // Testimonials (social proof)
  const testimonials = [
    {
      text: "As an advocate, ULJAAPP has transformed the way I guide my clients. The platform's structured resources and instant access to legal drafts save me hours every week, letting me focus on what matters most—delivering justice.",
      name: "Adv. Suresh Kumar",
      role: "Advocate"
    },
    {
      text: "ULJAAPP is a game-changer for law students like me. The clear explanations and real-world examples help bridge the gap between theory and practice. I feel more confident preparing for exams and internships.",
      name: "Priya Singh",
      role: "Law Student"
    },
    {
      text: "I never thought legal help could be this accessible. As a citizen, I found answers to my questions in simple language and even connected with a legal advisor for my property issue—all from my phone.",
      name: "Ramesh Patel",
      role: "Paralegal"
    }
  ];
  const [tIndex, setTIndex] = useState(0);
  useEffect(() => {
    const rot = setInterval(() => setTIndex(i => (i + 1) % testimonials.length), 6000);
    return () => clearInterval(rot);
  }, [testimonials.length]);

  return (
    <>
      <div className="content-wrapper home-no-sidenav">
        {/* Welcome banner restored */}
        <div className="welcome-banner">
          <h3 className="welcome-title" key={userName || 'default'}>
            {userName 
              ? <>
                  Hi <span className="notranslate" translate="no">{userName}</span>, Welcome to Universal Law and Justice App.
                </>
              : 'Welcome to Universal Law and Justice App'
            }
          </h3>
        </div>

        <section className="home-container">
          <div className="copy">
            <h1 className="home-title">bridge between law and life</h1>
            <p className="home-sub">Our mission is to make justice accessible and available to everyone</p>
            <div className="intro-text">
              <p>
                Whether you're a citizen seeking legal clarity or a legal practitioner deepening your expertise. Our platform is your guide to understanding the law, offering well-structured insights, practical solutions, and a space to explore, learn, and contribute. Empower yourself with knowledge, stay informed, and engage with the legal world in a way that truly matters.
              </p>
              <p>The Law isn't just written in books — It lives in the hands of those who seek to understand and uphold it.</p>
            </div>
            <div className="cta">
              <button className="columns highlights" onClick={() => navigate("/search")}>                
                <span className="icon-emoji notranslate" translate="no" aria-hidden="true">🔎</span>
                <span className="btn-text">Search For Legal Solutions</span>
              </button>
              <button className="columns highlights" onClick={() => navigate("/draft-create")}>                
                <span className="icon-emoji notranslate" translate="no" aria-hidden="true">📄</span>
                <span className="btn-text">Generate Legal Documents</span>
              </button>
              <button className="columns highlights" onClick={() => navigate("/contribute")}>                
                <span className="icon-emoji notranslate" translate="no" aria-hidden="true">✍️</span>
                <span className="btn-text">Share Your Legal Knowledge</span>
              </button>
            </div>
          </div>

          <div className="visual hero-slideshow" aria-live="polite">
            {heroImages.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={heroAlts[i]}
                loading={i === 0 ? "eager" : "lazy"}
                className={`hero-slide ${i === currentHero ? 'active' : ''}`}
              />
            ))}
          </div>
        </section>

        {/* Info sections: Uses, Advantages, Audience */}
        <section className="info-sections">
          <header className="section-head">
            <h2 className="section-title">Why ULJAAPP</h2>
            <p className="section-sub">Practical legal guidance, accessible to everyone</p>
          </header>

          <div className="info-grid">
            <div className="info-card">
              <div className="inline-head">{/* icon + title inline */}
                <div className="icon-badge scale-icon notranslate" translate="no" aria-hidden="true" data-icon="⚖️"></div>
                <h3>What you can do</h3>
              </div>
              <ul>
                <li>Search for legal solutions</li>
                <li>Generate legal documents and drafts</li>
                <li>Read curated articles and legal news</li>
                <li>Find and connect with legal advisors</li>
                <li>Save drafts and continue later</li>
                <li>Share your legal knowledge</li>
              </ul>
            </div>

            <div className="info-card">
              <div className="inline-head">
                <div className="icon-badge court-icon notranslate" translate="no" aria-hidden="true" data-icon="🏛️"></div>
                <h3>Why it helps</h3>
              </div>
              <ul>
                <li>Simple, structured guidance for quick clarity</li>
                <li>Up-to-date content across key jurisdictions</li>
                <li>Built-in language translation support</li>
                <li>Practical, action-oriented resources</li>
                <li>Free to start and easy to use</li>
                <li>Privacy-first approach</li>
              </ul>
            </div>

            <div className="info-card">
              <div className="inline-head">
                <div className="icon-badge judge-icon notranslate" translate="no" aria-hidden="true" data-icon="👨‍⚖️"></div>
                <h3>Who it's for</h3>
              </div>
              <ul>
                <li>Citizens and individuals</li>
                <li>Law students and learners</li>
                <li>Legal professionals and advisors</li>
                <li>Startups and businesses</li>
                <li>NGOs and social workers</li>
                <li>Researchers and analysts</li>
              </ul>
            </div>
          </div>

          <div className="stats-row" aria-label="ULJAAPP usage stats">
            <div className="stat"><span className="num">10k+</span><span className="label">Searches served</span></div>
            <div className="stat"><span className="num">1k+</span><span className="label">Drafts generated</span></div>
            <div className="stat"><span className="num">500+</span><span className="label">Contributors</span></div>
          </div>
        </section>

        {/* How It Works (moved below Why ULJAAPP) */}
        <section className="how-it-works" aria-labelledby="howItWorksTitle">
          <header className="hiw-head">
            <h2 id="howItWorksTitle">How It Works</h2>
            <p className="hiw-sub">Three simple steps from question to action</p>
          </header>
          <div className="hiw-steps">
            <div className="step-card">
              <div className="inline-head">
                <div className="step-icon notranslate" translate="no" aria-hidden="true">🔍</div>
                <h3>Search For Legal Solutions</h3>
              </div>
              <p>Enter an issue or keyword to explore structured legal guidance across jurisdictions.</p>
            </div>
            <div className="step-card">
              <div className="inline-head">
                <div className="step-icon notranslate" translate="no" aria-hidden="true">📄</div>
                <h3>Generate Legal Documents</h3>
              </div>
              <p>Create tailored drafts or documents you can refine or save for later review.</p>
            </div>
            <div className="step-card">
              <div className="inline-head">
                <div className="step-icon notranslate" translate="no" aria-hidden="true">✍️</div>
                <h3>Share Your Knowledge</h3>
              </div>
              <p>Contribute legal articles, insights, and expertise to help others in the community.</p>
            </div>
            <div className="step-card">
              <div className="inline-head">
                <div className="step-icon notranslate" translate="no" aria-hidden="true">👨‍⚖️</div>
                <h3>Connect</h3>
              </div>
              <p>Reach legal advisors for deeper clarification or next‑step validation.</p>
            </div>
            <div className="step-card">
              <div className="inline-head">
                <div className="step-icon notranslate" translate="no" aria-hidden="true">⚖️</div>
                <h3>Become a Legal Advisor</h3>
              </div>
              <p>Join our network of legal professionals and provide guidance to those seeking help.</p>
            </div>
            <div className="step-card">
              <div className="inline-head">
                <div className="step-icon notranslate" translate="no" aria-hidden="true">📰</div>
                <h3>News</h3>
              </div>
              <p>Stay updated with the latest legal developments, court decisions, and law changes.</p>
            </div>
          </div>
        </section>

        {/* Social Proof & Trust (moved) */}
        <section className="trust-social" aria-labelledby="trustTitle">
          <header className="trust-head">
            <h2 id="trustTitle">Trusted By Learners & Practitioners</h2>
            <p className="trust-sub">Built for clarity, privacy and accessibility</p>
          </header>
          <div className="trust-layout">
            <div className="testimonials" aria-live="polite">
              {testimonials.map((t, i) => (
                <div key={i} className={`testimonial-card ${i === tIndex ? 'active' : ''}`}>
                  <p className="t-text">“{t.text}”</p>
                  <p className="t-meta">— {t.name}, <span className="t-role">{t.role}</span></p>
                </div>
              ))}
              <div className="t-dots" role="tablist" aria-label="Testimonial selector">
                {testimonials.map((_, i) => (
                  <button key={i} aria-label={`Show testimonial ${i+1}`} aria-selected={i===tIndex} className={i===tIndex? 'on': ''} onClick={()=>setTIndex(i)} />
                ))}
              </div>
            </div>
            <ul className="trust-badges" aria-label="Platform trust highlights">
              <li className="badge-item"><span className="b-ico notranslate" aria-hidden="true" translate="no">🛡️</span><span>Privacy‑first drafts (not public)</span></li>
              <li className="badge-item"><span className="b-ico notranslate" aria-hidden="true" translate="no">🌐</span><span>Multi‑language support</span></li>
              <li className="badge-item"><span className="b-ico notranslate" aria-hidden="true" translate="no">⚡</span><span>Fast structured answers</span></li>
              <li className="badge-item"><span className="b-ico notranslate" aria-hidden="true" translate="no">🤝</span><span>Advisor network growing</span></li>
            </ul>
          </div>
          <p className="disclaimer" role="note">Disclaimer: Content is informational and not a substitute for professional legal advice.</p>
        </section>
      </div>

      {/* Chatbot for Help Center */}
      <Chatbot />

      {/* Removed <Footer /> since it's now global */}
    </>
  );
};

export default Home;